import { useEffect, useState, type ReactNode } from 'react';
import {
  useViewportBelow,
  MIN_VIEWPORT_WIDTH,
} from '@/hooks/use-viewport-gate';

/**
 * Desktop-only catch-all. Arcanum is a desktop-first underwriting terminal;
 * phones and portrait tablets can't render its dense layouts, so below
 * {@link MIN_VIEWPORT_WIDTH} we surface a full-screen "built for desktop"
 * notice — applied uniformly to the authenticated app, auth pages, AND public
 * deal links (`/deal/*`) per product direction (2026-06-06).
 *
 * NON-DESTRUCTIVE by design (2026-06-06 fix): once the app has rendered at an
 * adequate width it stays MOUNTED, and shrinking the viewport merely OVERLAYS
 * the notice on top. Earlier this component conditionally returned the notice
 * *instead of* `children`, which unmounted the whole tree on every resize
 * across the threshold — that tore down the Builder's Realtime channel
 * (`useConversationChannel`), and the re-subscribe on re-widen raced
 * supabase-js's async channel teardown so the status badge got stuck on
 * "Reconnecting" even though the (HTTP/SSE) chat path still worked. Overlaying
 * avoids the teardown entirely, and also preserves scroll / in-flight UI state.
 *
 * `hasRenderedApp` is a one-way latch: the provider/router tree is not booted
 * on a phone-first load (no auth/query traffic), but the instant the viewport
 * is wide enough once, the tree mounts and is never torn down by a subsequent
 * resize. The check is reactive (see `useViewportBelow`).
 */
export function DesktopOnlyGate({ children }: { children: ReactNode }) {
  const tooSmall = useViewportBelow();
  const [hasRenderedApp, setHasRenderedApp] = useState(!tooSmall);

  useEffect(() => {
    if (!tooSmall) setHasRenderedApp(true);
  }, [tooSmall]);

  return (
    <>
      {hasRenderedApp ? children : null}
      {tooSmall ? <DesktopOnlyNotice /> : null}
    </>
  );
}

function DesktopOnlyNotice() {
  return (
    <div
      data-testid="desktop-only-gate"
      role="alert"
      // z-index sits above every in-app layer, including the StatusDropdown
      // portal (inline zIndex 9999) which can be rendered in the mounted tree
      // beneath this overlay.
      className="fixed inset-0 z-[10000] flex flex-col items-center justify-center gap-6 bg-void px-6 text-center"
    >
      {/* ARCANUM mark — same two-bar accent glyph used on /login + the app loader */}
      <div className="flex items-center gap-3">
        <svg width="22" height="26" viewBox="0 0 16 18" aria-hidden="true">
          <rect
            x="0"
            y="0"
            width="5"
            height="18"
            rx="3"
            fill="var(--color-accent)"
          />
          <rect
            x="8"
            y="0"
            width="5"
            height="18"
            rx="3"
            fill="var(--color-accent)"
          />
        </svg>
        <div className="flex flex-col items-start leading-none">
          <span className="font-mono text-base font-bold tracking-[0.18em] text-text-bright">
            ARCANUM
          </span>
          <span className="mt-1 font-mono text-[9px] tracking-[0.3em] text-accent">
            AI UNDERWRITING
          </span>
        </div>
      </div>

      <div className="flex max-w-md flex-col gap-3">
        <h1 className="font-body text-xl font-semibold text-text-bright">
          Built for desktop
        </h1>
        <p className="font-body text-sm leading-relaxed text-text-muted">
          Arcanum is a desktop-first underwriting terminal. Its dense,
          data-heavy views need a larger screen to render properly — please open
          it on a laptop or desktop.
        </p>
      </div>

      <p className="font-mono text-[11px] tracking-wide text-text-dim">
        Minimum width {MIN_VIEWPORT_WIDTH}px — or widen your browser window.
      </p>
    </div>
  );
}
